import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useOutletContext, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Rocket, Sparkles, Trash2, ChevronRight, Loader2, X, ShieldCheck, CheckCircle2, Circle, Mail, Puzzle } from "lucide-react";
import { PageContainer, PageHeader, Button, EmptyState } from "../components/kit";
import ApplyModal from "../components/ApplyModal";
import { CHROME_STORE_URL } from "../lib/extension";

// One-click apply — application tracker. Reads the drafts stored via /apply/drafts
// and presents them as an "applied by AI" table: company, resume + cover-letter
// readiness, status, and when it was applied. Reopen a row to review/approve.

const STATUS_META = {
  preparing:       { label: "Preparing…",       dot: "#0025D8" },
  draft:           { label: "Needs review",     dot: "#F59E0B" },
  approved:        { label: "Approved",          dot: "#10B981" },
  queued:          { label: "Queued",            dot: "#2563EB" },
  ext_queued:      { label: "Applying in your browser…", dot: "#0025D8" },
  submitting:      { label: "Submitting",        dot: "#2563EB" },
  submitted:       { label: "Submitted",         dot: "#10B981" },
  // Employer sent a confirmation email → ground-truth "they received it".
  confirmed:       { label: "Confirmed",         dot: "#0FA968" },
  // Filled through Review — ready for the candidate to review + confirm submission.
  awaiting_review:    { label: "Ready for review",   dot: "#8B5CF6" },
  needs_verification: { label: "Verify your email",  dot: "#F59E0B" },
  dry_run:            { label: "Prepared (dry run)", dot: "#6366F1" },
  needs_user:      { label: "Action needed",     dot: "#F59E0B" },
  // Candidate applied on the employer's own site — we hold the record, we never sent anything.
  applied_manually: { label: "Self-applied",      dot: "#7C3AED" },
  // Dayforce handed to the admin for account setup — soft, friendly (never expose 'needs_account').
  needs_account:   { label: "Working on it",     dot: "#0025D8" },
  skipped:         { label: "Skipped",           dot: "#64748B" },
  failed:          { label: "Failed",            dot: "#E11D48" },
};

// Statuses that advance on their own — the background queue is still working on them. These drive
// the live auto-refresh (poll while any exist) and get a pulsing dot so you can see they're moving.
const LIVE_STATUSES = new Set(["preparing", "queued", "ext_queued", "submitting", "approved"]);

// Browser-lane ATSes apply in the candidate's own browser (via the extension), so their in-flight
// statuses read "Applying in your browser…" instead of the generic "Queued/Submitting" — the opening
// tab + short wait then feel intentional, not broken.
const EXT_LANE_ATS = new Set(["ashby", "icims", "greenhouse", "lever", "smartrecruiters"]);

// Shown ONLY to a candidate who has browser-lane applications waiting and has never connected the
// extension. That pairing matters: those rows render as "Applying in your browser…" and pulse like
// something is happening, when in fact nothing can happen until the extension exists to pick them
// up. Without this the page quietly lies to exactly the people it is failing.
//
// The install is otherwise offered only on the last screen of /apply/setup, which every existing
// candidate has already passed — so before this, nobody who onboarded before the extension shipped
// had any route to it.
function ExtensionNeeded({ count }) {
  return (
    <div className="mb-4 rounded-2xl border border-[#0025D8]/25 bg-[#0025D8]/[0.04] p-4 flex items-start gap-3">
      <span className="w-9 h-9 rounded-xl bg-[#0025D8]/10 grid place-items-center flex-shrink-0">
        <Puzzle className="w-4 h-4 text-[#0025D8]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-[#0F172A]">
          {count === 1
            ? "1 application is waiting for the browser extension"
            : `${count} applications are waiting for the browser extension`}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          These employers only accept an application sent from your own browser. Add the extension
          and they will be submitted automatically — nothing else to do.
        </p>
      </div>
      <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer"
         className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#0025D8] hover:bg-[#001FB0] text-white px-3.5 py-2 text-xs font-bold transition-colors">
        <Puzzle className="w-3.5 h-3.5" /> Add to Chrome
      </a>
    </div>
  );
}

// Status indicator. In-flight statuses pulse; when a status changes between refreshes the pill
// briefly fades in (`flash`) so the transition is visible without a page reload.
function StatusPill({ status, ats, flash, confirmed }) {
  const browserLane = EXT_LANE_ATS.has(ats) && ["ext_queued", "queued", "submitting"].includes(status);
  // An employer confirmation is GROUND TRUTH — they have the application, whatever our own status
  // says. Requiring status === "submitted" too meant a run that really did reach the employer but
  // finished on some other status still read "Ready for review", telling the candidate to go and do
  // something they'd already done. This matches _apply_stage(), which returns "confirmed" on the
  // confirmation alone.
  const meta = confirmed
    ? STATUS_META.confirmed                                 // employer acknowledged receipt
    : browserLane
      ? { label: "Applying in your browser…", dot: "#0025D8" }
      : (STATUS_META[status] || STATUS_META.draft);
  const live = !confirmed && (browserLane || LIVE_STATUSES.has(status));
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium text-[#0F172A] ${flash ? "at-pill-in" : ""}`}>
      <span className="relative flex items-center justify-center w-2 h-2">
        {live && (
          <span className="absolute inline-flex w-2 h-2 rounded-full at-ping" style={{ backgroundColor: meta.dot }} />
        )}
        <span className="relative w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.dot }} />
      </span>
      {meta.label}
    </span>
  );
}

const FILTERS = [
  { k: "all", label: "All" },
  { k: "draft", label: "Needs review" },
  { k: "approved", label: "Approved" },
  { k: "submitted", label: "Submitted" },
];

const AVATAR_COLORS = ["#0025D8", "#7C3AED", "#DB2777", "#059669", "#D97706", "#0891B2", "#4F46E5", "#E11D48"];

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name || "?").slice(0, 2).toUpperCase();
};
const avatarColor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const scoreColor = (s) => (s == null ? "#94A3B8" : s >= 70 ? "#059669" : s >= 50 ? "#D97706" : "#94A3B8");

// Company avatar: the resolved favicon logo, falling back to a colored initial tile (same as the
// "Ready to apply on Autopilot" cards) when there's no logo or the image fails to load. App-icon style —
// soft-rounded tile, inner padding so the favicon breathes, subtle border + shadow for depth.
function CompanyAvatar({ company, title, logo }) {
  const [err, setErr] = useState(false);
  const base = "w-9 h-9 rounded-xl flex-shrink-0 border border-border/70 shadow-sm ring-1 ring-black/[0.02]";
  if (logo && !err) {
    return <img src={logo} alt="" loading="lazy" onError={() => setErr(true)}
      className={`${base} object-contain bg-white p-1.5`} />;
  }
  return (
    <div className={`${base} grid place-items-center text-white text-[11px] font-bold`}
      style={{ background: `linear-gradient(145deg, ${avatarColor(company || title || "?")}, ${avatarColor(company || title || "?")}cc)` }}>
      {initials(company || title)}
    </div>
  );
}

// The "Applied" timestamp: latest activity (submit → approve → create). Used for the column AND the sort.
const whenOf = (d) => d.updated_at || d.approved_at || d.created_at || "";

const timeAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} hour${h !== 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d} day${d !== 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

function ReadyCell({ ready }) {
  return ready ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-[#047857]">
      <CheckCircle2 className="w-3.5 h-3.5" /> Ready
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Circle className="w-3.5 h-3.5" /> —
    </span>
  );
}

// Résumé match: original résumé vs the version we tailored for this job. "before" is a real
// measurement; "after" is the optimizer's estimate (hence the tooltip note).
function MatchCell({ before, after }) {
  const b = before != null ? Math.round(before) : null;
  const a = after != null ? Math.round(after) : null;
  if (a == null && b == null) return <span className="text-xs text-muted-foreground">—</span>;
  if (a == null) return <span className="text-sm font-bold" style={{ color: scoreColor(b) }}>{b}%</span>;
  const improved = b != null && a > b;
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap"
      title="Estimated ATS match — your original résumé vs the version we tailored for this job (after = AI estimate)">
      {b != null && <span className="text-xs text-muted-foreground">{b}%</span>}
      {b != null && <span className="text-muted-foreground text-xs">→</span>}
      <span className="text-sm font-bold" style={{ color: scoreColor(a) }}>{a}%</span>
      {improved && <span className="text-[10px] font-bold text-[#059669]">↑{a - b}</span>}
    </div>
  );
}

// ── first-load skeleton ──────────────────────────────────────────────────────
// The table's own chrome (border, column headers, row rhythm) renders for real and only the VALUES
// are placeholders, so nothing shifts when the data lands. Deliberately no rotating status messages:
// the jobs grid narrates genuinely multi-second work (fetch → score → rank), whereas this is a single
// read of applications that already exist, and captioning a 200ms fetch with invented stages would
// be theatre.
const SKELETON_DELAY_MS = 400;   // a fast fetch beats this — show nothing rather than flash
const SKELETON_ROWS = 6;

function useAfterDelay(active, ms = SKELETON_DELAY_MS) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!active) { setReady(false); return; }
    const t = setTimeout(() => setReady(true), ms);
    return () => clearTimeout(t);
  }, [active, ms]);
  return ready;
}

// `animate` is off below the fold: a shimmer repaints every frame, and six rows x eight cells is
// enough of them to cost more than it gives on a mid-range phone.
function Bar({ w, animate = true, className = "" }) {
  return <div className={`h-3 rounded bg-[#0F172A]/[0.07] ${animate ? "vh-shimmer" : ""} ${className}`} style={{ width: w }} />;
}

function SkeletonRow({ animate = true }) {
  return (
    <tr className="border-b border-border/60 last:border-b-0">
      <td className="px-3 py-3.5"><div className={`w-4 h-4 rounded bg-[#0F172A]/[0.07] ${animate ? "vh-shimmer" : ""}`} /></td>
      <td className="px-3 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg bg-[#0F172A]/[0.07] flex-shrink-0 ${animate ? "vh-shimmer" : ""}`} />
          <div className="flex flex-col gap-1.5 min-w-0">
            <Bar w="120px" animate={animate} />
            <Bar w="84px" animate={animate} className="h-2.5 opacity-70" />
          </div>
        </div>
      </td>
      <td className="px-3 py-3.5"><Bar w="52px" animate={animate} /></td>
      <td className="px-3 py-3.5"><Bar w="64px" animate={animate} /></td>
      <td className="px-3 py-3.5"><Bar w="64px" animate={animate} /></td>
      <td className="px-3 py-3.5"><div className={`h-6 w-[92px] rounded-full bg-[#0F172A]/[0.07] ${animate ? "vh-shimmer" : ""}`} /></td>
      <td className="px-3 py-3.5"><Bar w="70px" animate={animate} /></td>
      <td className="px-3 py-3.5"><Bar w="16px" animate={animate} /></td>
    </tr>
  );
}

export default function ApplyTracker() {
  const { embedded } = useOutletContext() || {};
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState(null);   // null = new application
  const [initialUrl, setInitialUrl] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(new Set());   // draft_ids for batch approve
  const [consented, setConsented] = useState(true);       // refined on load
  const [consentChecked, setConsentChecked] = useState(false);
  const [approving, setApproving] = useState(false);
  const [batch, setBatch] = useState(null);   // live bulk-prepare progress
  const [flashIds, setFlashIds] = useState(() => new Set());   // rows whose status just changed
  // null until the first load answers it — so the prompt can't flash on during the initial fetch
  // for someone who does have the extension.
  const [extConnected, setExtConnected] = useState(null);
  const prevStatusRef = useRef(null);   // draft_id -> last-seen status (null until first load)
  const navigate = useNavigate();
  // `loading` is only ever true on the initial mount — load() clears it and never re-raises it, so
  // the 4s live poll updates rows in place and can never flash this skeleton back over the table.
  const skeletonReady = useAfterDelay(loading);

  const load = () => {
    api.get("/apply/drafts")
      .then(({ data }) => {
        setDrafts(data.drafts || []);
        // Polled every 4s alongside the rows, so installing the extension makes the banner
        // disappear on its own within a few seconds of its first backend poll — no refresh.
        setExtConnected(Boolean(data.extension_connected));
      })
      .catch(() => toast.error("Could not load your applications"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.post("/apply/mark-seen").catch(() => {});   // clears the 'applied' nav badge
    api.get("/apply/profile")
      .then(({ data }) => setConsented(!!data.profile?.apply_authorization?.agreed))
      .catch(() => {});
  }, []);

  // Whenever drafts refresh, diff each row's status against the previous snapshot and briefly
  // flash the ones that changed (pill fade-in + row glow). Seeded silently on first load.
  useEffect(() => {
    const cur = {};
    drafts.forEach((d) => { cur[d.draft_id] = d.status; });
    const prev = prevStatusRef.current;
    prevStatusRef.current = cur;
    if (prev === null) return;   // first load — seed, don't flash everything
    const changed = Object.keys(cur).filter((id) => prev[id] !== undefined && prev[id] !== cur[id]);
    if (!changed.length) return;
    setFlashIds((s) => { const n = new Set(s); changed.forEach((id) => n.add(id)); return n; });
    const t = setTimeout(() => {
      setFlashIds((s) => { const n = new Set(s); changed.forEach((id) => n.delete(id)); return n; });
    }, 1500);
    return () => clearTimeout(t);
  }, [drafts]);

  // Smart auto-refresh: while any application is still moving through the queue, quietly re-fetch
  // every few seconds so statuses update without a manual refresh. Pauses when the tab is hidden
  // (and refreshes immediately on return); stops entirely once everything is in a terminal state.
  const hasLive = drafts.some((d) => LIVE_STATUSES.has(d.status));
  useEffect(() => {
    if (!hasLive) return;
    const timer = setInterval(() => { if (!document.hidden) load(); }, 4000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [hasLive]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Live tracking of a bulk-prepare batch handed off from Matches: poll progress and
  // refresh the table as drafts land, so the candidate watches them appear in real time.
  const trackBatch = (batchId) => {
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    (async () => {
      for (let i = 0; i < 160; i++) {
        let b;
        try { ({ data: b } = await api.get(`/apply/bulk-prepare/${batchId}`)); }
        catch { await sleep(1500); continue; }
        const done = (b.ready_count || 0) + (b.failed_count || 0);
        setBatch({ total: b.total, done, ready: b.ready_count || 0, failed: b.failed_count || 0, status: b.status });
        load();  // new drafts appear as items complete
        if (b.status === "done") { setTimeout(() => setBatch(null), 6000); return; }
        await sleep(1500);
      }
      setBatch(null);
    })();
  };

  // Deep-links: ?url=<job> opens a fresh tailoring modal; ?open=<id> reopens a draft;
  // ?batch=<id> live-tracks a bulk batch (handed off from the Matches page).
  useEffect(() => {
    const url = searchParams.get("url");
    const openId2 = searchParams.get("open");
    const batchId = searchParams.get("batch");
    if (url) { setOpenId(null); setInitialUrl(url); setModalOpen(true); }
    if (openId2) { setOpenId(openId2); setInitialUrl(""); setModalOpen(true); }
    if (batchId) { trackBatch(batchId); }
    if (url || openId2 || batchId) {
      ["url", "open", "batch"].forEach((k) => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setOpenId(null); setInitialUrl(""); setModalOpen(true); };

  const markManual = async (draftId, applied) => {
    setDrafts((ds) => ds.map((x) => x.draft_id === draftId
      ? { ...x, applied_manually_at: applied ? new Date().toISOString() : null } : x));
    try {
      await api.post(`/apply/draft/${draftId}/applied-manually`, { applied });
      toast.success(applied ? "Got it — we won't apply to this one again." : "We can apply to this one again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update this application");
      load();
    }
  };
  const openDraft = (id) => {
    const d = drafts.find((x) => x.draft_id === id);
    // A pre-apply "draft" opens the create/edit modal; everything post-apply (review, needs-you,
    // submitted, confirmed, failed) opens the full status PAGE (receipt + review actions + mailbox).
    if (d && d.status === "draft") { setOpenId(id); setInitialUrl(""); setModalOpen(true); return; }
    navigate(`/apply/status/${id}`);
  };

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const batchApprove = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!consented && !consentChecked) {
      toast.error("Please authorize AI applications to approve them.");
      return;
    }
    setApproving(true);
    try {
      if (!consented) { await api.post("/apply/consent", { agreed: true }); setConsented(true); }
      const { data } = await api.post("/apply/drafts/batch-approve", { draft_ids: ids });
      const n = data.approved_count || 0;
      toast.success(`${n} application${n !== 1 ? "s" : ""} approved.`);
      setSelected(new Set());
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not approve.");
    } finally { setApproving(false); }
  };

  const del = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this application?")) return;
    setBusyId(id);
    try {
      await api.delete(`/apply/draft/${id}`);
      setDrafts((prev) => prev.filter((d) => d.draft_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Could not delete"); }
    finally { setBusyId(null); }
  };

  const shown = drafts
    .filter((d) => filter === "all" || d.status === filter)
    .sort((a, b) => whenOf(b).localeCompare(whenOf(a)));   // most recently applied first
  const counts = drafts.reduce((a, d) => ({ ...a, [d.status]: (a[d.status] || 0) + 1 }), {});
  const draftIds = drafts.filter((d) => d.status === "draft").map((d) => d.draft_id);
  const allDraftsSelected = draftIds.length > 0 && draftIds.every((id) => selected.has(id));
  const selectAllDrafts = () => setSelected(allDraftsSelected ? new Set() : new Set(draftIds));

  const header = (
    <PageHeader
      back={{ to: "/apply/settings", label: "Settings" }}
      eyebrow="One-click apply"
      title="Applications."
      subtitle="Jobs you've applied to on Autopilot — tailored resume, cover letter, and status, all tracked here."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate("/apply/inbox")}><Mail className="w-3.5 h-3.5" /> Inbox</Button>
          <Button variant="navy" size="sm" onClick={openNew}><Sparkles className="w-3.5 h-3.5" /> Apply to a job link</Button>
        </div>
      }
    />
  );

  // Only the rows that are genuinely stuck: browser-lane ATS, parked in ext_queued, with no
  // extension to pick them up. A draft of any other status is unaffected and must not inflate this.
  const waitingOnExt = extConnected === false
    ? drafts.filter((d) => d.status === "ext_queued" && EXT_LANE_ATS.has(d.ats)).length
    : 0;

  const body = (
    <>
      {waitingOnExt > 0 && <ExtensionNeeded count={waitingOnExt} />}
      <style>{`
        @keyframes atRowGlow { 0% { background-color: rgba(0,37,216,0.10); } 100% { background-color: transparent; } }
        .at-row-glow { animation: atRowGlow 1.5s ease-out; }
        @keyframes atPillIn { 0% { opacity: 0; transform: translateY(-3px); } 100% { opacity: 1; transform: translateY(0); } }
        .at-pill-in { animation: atPillIn 0.5s ease-out; }
        @keyframes atPing { 0% { transform: scale(1); opacity: 0.5; } 75%, 100% { transform: scale(2.6); opacity: 0; } }
        .at-ping { animation: atPing 1.6s cubic-bezier(0,0,0.2,1) infinite; }
        @keyframes atLiveBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        .at-live-dot { animation: atLiveBlink 1.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .at-row-glow, .at-pill-in, .at-ping, .at-live-dot { animation: none !important; }
        }
      `}</style>
      {embedded && (
        <div className="flex justify-end mb-4">
          <Button variant="navy" size="sm" onClick={openNew}><Sparkles className="w-3.5 h-3.5" /> Apply to a job link</Button>
        </div>
      )}

      <ApplyModal open={modalOpen} draftId={openId} initialUrl={initialUrl} onClose={() => setModalOpen(false)} onChanged={load} />


      {/* Live bulk-prepare progress (handed off from Matches) */}
      {batch && (
        <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 mb-4 ${
          batch.status === "done" ? "border-[#10B981]/30 bg-[#10B981]/5" : "border-[#0025D8]/30 bg-[#0025D8]/5"}`}>
          {batch.status === "done" ? (
            <CheckCircle2 className="w-5 h-5 text-[#059669] flex-shrink-0" />
          ) : (
            <Loader2 className="w-5 h-5 text-[#0025D8] animate-spin flex-shrink-0" />
          )}
          <div className="text-sm">
            {batch.status === "done" ? (
              <span className="text-[#0F172A]">
                <span className="font-semibold">{batch.ready} of {batch.total}</span> application{batch.total !== 1 ? "s" : ""} prepared — review and approve below.
                {batch.failed > 0 && <span className="text-muted-foreground"> {batch.failed} couldn't be prepared.</span>}
              </span>
            ) : (
              <span className="text-[#0F172A]">
                Preparing your applications on Autopilot… <span className="font-semibold">{batch.done} of {batch.total}</span> ready. You can leave this page — it keeps working.
              </span>
            )}
          </div>
        </div>
      )}

      {loading ? (
        // Nothing at all under the delay — a skeleton that appears and vanishes inside 400ms reads
        // as a flicker, which is worse than a brief blank.
        !skeletonReady ? <div className="h-[420px]" /> : (
          <>
            <div className="flex items-center gap-2 mb-4">
              {FILTERS.map((f, i) => (
                <div key={f.k}
                  className={`font-mono text-[10px] px-2.5 py-1 rounded-lg border ${
                    i === 0 ? "bg-[#0F172A]/[0.07] border-transparent" : "border-border"}`}>
                  {/* the labels are known up front, so show them — only the counts are unknown */}
                  <span className="opacity-40">{f.label}</span>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-border bg-white overflow-hidden mb-24">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead>
                    <tr className="bg-secondary/40 border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                      <th className="w-10 px-3 py-3"></th>
                      <th className="px-3 py-3 font-medium">Company</th>
                      <th className="px-3 py-3 font-medium">Résumé match</th>
                      <th className="px-3 py-3 font-medium">Resume</th>
                      <th className="px-3 py-3 font-medium">Cover letter</th>
                      <th className="px-3 py-3 font-medium">Status</th>
                      <th className="px-3 py-3 font-medium">Applied</th>
                      <th className="w-10 px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                      <SkeletonRow key={i} animate={i < 4} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )
      ) : (drafts.length === 0 && !batch) ? (
        <EmptyState
          icon={Rocket}
          title="No applications yet"
          body="Paste a link to any job you've found and we'll tailor your résumé, write the cover letter and answer the screening questions — or use ⚡ Apply on Autopilot from your job matches."
          action={<Button onClick={openNew}><Sparkles className="w-4 h-4" /> Apply to a job link</Button>}
        />
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            {FILTERS.map((f) => (
              <button key={f.k} onClick={() => setFilter(f.k)}
                className={`font-mono text-[10px] px-2.5 py-1 rounded-lg border transition-colors ${
                  filter === f.k ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-border text-muted-foreground hover:bg-secondary"
                }`}>
                {f.label}{f.k !== "all" && counts[f.k] ? ` · ${counts[f.k]}` : ""}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              {hasLive && (
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.12em] text-[#0025D8] bg-[#0025D8]/5 border border-[#0025D8]/20 rounded-lg px-2 py-1"
                  title="Live — statuses update automatically while jobs are being applied">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0025D8] at-live-dot" />
                  Live · updating
                </span>
              )}
              {draftIds.length > 0 && (
                <button onClick={selectAllDrafts}
                  className="font-mono text-[10px] px-2.5 py-1 rounded-lg border border-border text-muted-foreground hover:bg-secondary">
                  {allDraftsSelected ? "Clear selection" : `Select all needs-review · ${draftIds.length}`}
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-white overflow-hidden mb-24">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="bg-secondary/40 border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    <th className="w-10 px-3 py-3"></th>
                    <th className="px-3 py-3 font-medium">Company</th>
                    <th className="px-3 py-3 font-medium">Résumé match</th>
                    <th className="px-3 py-3 font-medium">Resume</th>
                    <th className="px-3 py-3 font-medium">Cover letter</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                    <th className="px-3 py-3 font-medium">Applied</th>
                    <th className="w-10 px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {shown.map((d) => {
                    const when = whenOf(d);
                    return (
                      <tr key={d.draft_id} onClick={() => openDraft(d.draft_id)}
                        className={`group hover:bg-secondary/30 cursor-pointer transition-colors ${flashIds.has(d.draft_id) ? "at-row-glow" : ""}`}>
                        <td className="px-3 py-3.5 align-middle" onClick={(e) => e.stopPropagation()}>
                          {d.status === "draft" && (
                            <input type="checkbox" checked={selected.has(d.draft_id)}
                              onChange={(e) => toggleSelect(e, d.draft_id)}
                              className="h-4 w-4 rounded border-border text-[#0025D8] focus:ring-[#0025D8] cursor-pointer"
                              title="Select for batch approve" />
                          )}
                        </td>
                        <td className="px-3 py-3.5">
                          <div className="flex items-center gap-3 min-w-0">
                            <CompanyAvatar company={d.company} title={d.title} logo={d.logo} />
                            <div className="min-w-0">
                              <div className="font-semibold text-[#0F172A] truncate group-hover:text-[#0025D8] transition-colors">
                                {d.company || "—"}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{d.title || "Role"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3.5"><MatchCell before={d.match_score} after={d.post_ats_score} /></td>
                        <td className="px-3 py-3.5"><ReadyCell ready={d.has_resume} /></td>
                        <td className="px-3 py-3.5"><ReadyCell ready={d.has_cover_letter} /></td>
                        <td className="px-3 py-3.5">
                          <StatusPill status={d.status} ats={d.ats} confirmed={d.confirmed} flash={flashIds.has(d.draft_id)} />
                          {/* A failed job the candidate then applied to by hand. Marking it stops us
                              ever re-applying — including an admin re-run after a fix — because a
                              duplicate application cannot be withdrawn. */}
                          {d.confirmed && ["failed", "needs_user"].includes(d.status) ? (
                            // The employer emailed a confirmation, which beats a stale "failed" —
                            // offering "Self-applied" or a retry here would be nonsense, and
                            // retrying would send them a duplicate.
                            <span className="mt-1 block text-[10px] text-[#0FA968] font-semibold"
                              title="The employer confirmed they received this application">
                              employer confirmed receipt
                            </span>
                          ) : d.applied_manually_at ? (
                            <button onClick={(e) => { e.stopPropagation(); markManual(d.draft_id, false); }}
                              title="Undo — let us apply for you again"
                              className="mt-1 block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100">
                              Self-applied · undo
                            </button>
                          ) : (["failed", "needs_user"].includes(d.status) && !d.confirmed) ? (
                            <>
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/support?draft=${d.draft_id}`); }}
                              title="Tell us what went wrong with this one"
                              className="mt-1 block text-[10px] font-semibold text-[#0025D8] hover:underline">
                              Report a problem
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); markManual(d.draft_id, true); }}
                              title="Tell us you applied yourself, so we never send a duplicate"
                              className="mt-1 block text-[10px] font-semibold text-muted-foreground hover:text-[#0F172A] underline decoration-dotted">
                              Self-applied
                            </button>
                            </>
                          ) : null}
                        </td>
                        <td className="px-3 py-3.5 text-xs text-muted-foreground whitespace-nowrap">{timeAgo(when)}</td>
                        <td className="px-3 py-3.5">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={(e) => del(e, d.draft_id)} disabled={busyId === d.draft_id}
                              className="text-muted-foreground hover:text-[#E11D48] p-1" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#0025D8] transition-colors" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Sticky batch-approve bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-border bg-white/95 backdrop-blur-sm shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
          <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-semibold text-sm text-[#0F172A]">{selected.size} selected</span>
              <button onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-[#0F172A] inline-flex items-center gap-1">
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {!consented && (
                <label className="flex items-center gap-2 text-[11px] text-muted-foreground max-w-xs cursor-pointer">
                  <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-border text-[#0025D8] flex-shrink-0" />
                  I authorize VetraHub to prepare these applications on my behalf; I've reviewed them.
                </label>
              )}
              <button onClick={batchApprove} disabled={approving || (!consented && !consentChecked)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-[#10B981] text-white hover:bg-[#059669] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0">
                {approving ? <><Loader2 className="w-4 h-4 animate-spin" /> Approving…</> : <><ShieldCheck className="w-4 h-4" /> Approve {selected.size}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return embedded
    ? <div>{body}</div>
    : <PageContainer width="wide">{header}{body}</PageContainer>;
}
